import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Slider,
  Chip,
  Card,
  CardContent,
  Alert,
  CircularProgress
} from '@mui/material';
import { generateAssessment } from 'services/vendor-assessment-service';
import { getVendors } from 'services/vendor-service';
import { getFrameworks } from 'services/frameworks-service';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';

const maturityLevels = [
  { value: 0, label: 'L0 - Not Performed', description: 'No process in place' },
  { value: 1, label: 'L1 - Initial', description: 'Ad-hoc processes' },
  { value: 2, label: 'L2 - Managed', description: 'Documented and followed' },
  { value: 3, label: 'L3 - Defined', description: 'Standardized across organization' },
  { value: 4, label: 'L4 - Quantitatively Managed', description: 'Measured and controlled' },
  { value: 5, label: 'L5 - Optimizing', description: 'Continuously improving' }
];

/**
 * AssessmentWizard Component
 * Multi-step wizard for creating vendor assessments
 */
export const AssessmentWizard = ({ onSuccess, onCancel, tenant }) => {
  const { t } = useTranslation(['gap-analysis']);
  const queryClient = useQueryClient();
  const SwalReact = withReactContent(Swal);

  const steps = [
    t('gap-analysis:assessment.wizard.steps.step1'),
    t('gap-analysis:assessment.wizard.steps.step2'),
    t('gap-analysis:assessment.wizard.steps.step3'),
  ];

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    vendorId: '',
    frameworkId: '',
    targetMaturityLevel: 3,
    internalNotes: ''
  });

  // Fetch vendors
  const { data: vendorsData, isLoading: vendorsLoading } = useQuery(
    ['vendors', tenant],
    () => getVendors(tenant),
    {
      enabled: !!tenant
    }
  );

  // Fetch frameworks
  const { data: frameworksData, isLoading: frameworksLoading } = useQuery(
    ['frameworks'],
    () => getFrameworks(),
    {
      staleTime: 5 * 60 * 1000 // Cache for 5 minutes
    }
  );

  const vendors = vendorsData?.data?.data || [];
  const frameworks = frameworksData?.data || [];
  const selectedVendor = vendors.find(v => v._id === formData.vendorId);
  const selectedFramework = frameworks.find(f => f._id === formData.frameworkId);

  // Generate assessment mutation
  const generateMutation = useMutation(
    (data) => generateAssessment(data),
    {
      onSuccess: (response) => {
        // Check if the API returned success: true in the response body
        if (response.data?.success === false) {
          // API returned an error in the response body
          SwalReact.fire({
            icon: 'error',
            title: t('gap-analysis:assessment.wizard.swal.errorTitle'),
            text: response.data?.error || t('gap-analysis:assessment.wizard.swal.errorText'),
            customClass: {
              container: 'swal-high-z-index'
            }
          });
          return;
        }

        queryClient.invalidateQueries('assessments');
        SwalReact.fire({
          icon: 'success',
          title: t('gap-analysis:assessment.wizard.swal.createdTitle'),
          html: `<p>${t('gap-analysis:assessment.wizard.swal.createdHtml', { count: response.data?.data?.controlCount || 0 })}</p>`,
          timer: 3000,
          showConfirmButton: true,
          customClass: {
            container: 'swal-high-z-index'
          }
        });
        if (onSuccess) onSuccess(response.data);
      },
      onError: (error) => {
        SwalReact.fire({
          icon: 'error',
          title: t('gap-analysis:assessment.wizard.swal.errorTitle'),
          text: error.response?.data?.error || error.response?.data?.message || t('gap-analysis:assessment.wizard.swal.errorText'),
          customClass: {
            container: 'swal-high-z-index'
          }
        });
      }
    }
  );

  const handleNext = () => {
    // Validation for each step
    if (activeStep === 0 && !formData.vendorId) {
      SwalReact.fire({
        icon: 'warning',
        title: t('gap-analysis:assessment.wizard.swal.noVendorTitle'),
        text: t('gap-analysis:assessment.wizard.swal.noVendorText'),
        customClass: {
          container: 'swal-high-z-index'
        }
      });
      return;
    }

    if (activeStep === 1 && !formData.frameworkId) {
      SwalReact.fire({
        icon: 'warning',
        title: t('gap-analysis:assessment.wizard.swal.noFrameworkTitle'),
        text: t('gap-analysis:assessment.wizard.swal.noFrameworkText'),
        customClass: {
          container: 'swal-high-z-index'
        }
      });
      return;
    }

    if (activeStep === steps.length - 1) {
      handleSubmit();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = () => {
    const submitData = {
      vendorId: formData.vendorId,
      frameworkId: formData.frameworkId,
      targetMaturityLevel: formData.targetMaturityLevel,
      internalNotes: formData.internalNotes
    };

    generateMutation.mutate(submitData);
  };

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
  };

  const handleMaturityChange = (event, newValue) => {
    setFormData({
      ...formData,
      targetMaturityLevel: newValue
    });
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('gap-analysis:assessment.wizard.step1.title')}
            </Typography>
            <Typography variant="body2" color="textSecondary" mb={3}>
              {t('gap-analysis:assessment.wizard.step1.subtitle')}
            </Typography>

            {vendorsLoading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <FormControl fullWidth>
                <InputLabel>{t('gap-analysis:assessment.wizard.step1.vendorLabel')}</InputLabel>
                <Select
                  value={formData.vendorId}
                  onChange={handleChange('vendorId')}
                  label={t('gap-analysis:assessment.wizard.step1.vendorLabel')}
                >
                  {vendors.map((vendor) => (
                    <MenuItem key={vendor._id} value={vendor._id}>
                      <Box display="flex" justifyContent="space-between" width="100%">
                        <span>{vendor.name}</span>
                        <Chip
                          label={`Tier ${vendor.tier}`}
                          size="small"
                          color={vendor.tier === 1 ? 'error' : vendor.tier === 2 ? 'warning' : 'success'}
                        />
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {selectedVendor && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>{t('gap-analysis:assessment.wizard.step1.tier', { tier: selectedVendor.tier })}</strong> -
                  {selectedVendor.tier === 1 && ` ${t('gap-analysis:assessment.wizard.step1.tier1Alert')}`}
                  {selectedVendor.tier === 2 && ` ${t('gap-analysis:assessment.wizard.step1.tier2Alert')}`}
                  {selectedVendor.tier === 3 && ` ${t('gap-analysis:assessment.wizard.step1.tier3Alert')}`}
                </Typography>
              </Alert>
            )}

            {selectedVendor?.tier === 3 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  {t('gap-analysis:assessment.wizard.step1.tier3Warning')}
                </Typography>
              </Alert>
            )}
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('gap-analysis:assessment.wizard.step2.title')}
            </Typography>
            <Typography variant="body2" color="textSecondary" mb={3}>
              {t('gap-analysis:assessment.wizard.step2.subtitle')}
            </Typography>

            {frameworksLoading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>{t('gap-analysis:assessment.wizard.step2.frameworkLabel')}</InputLabel>
                <Select
                  value={formData.frameworkId}
                  onChange={handleChange('frameworkId')}
                  label={t('gap-analysis:assessment.wizard.step2.frameworkLabel')}
                >
                  {frameworks.map((framework) => (
                    <MenuItem key={framework._id} value={framework._id}>
                      <Box>
                        <Typography variant="body1">
                          {framework.name} {framework.version && `(${framework.version})`}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {framework.source} {framework.geography && `- ${t(`gap-analysis:${framework.geography}`)}`}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Box mb={3}>
              <Typography variant="subtitle2" gutterBottom>
                {t('gap-analysis:assessment.wizard.step2.targetMaturity')} {maturityLevels[formData.targetMaturityLevel].label}
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block" mb={2}>
                {maturityLevels[formData.targetMaturityLevel].description}
              </Typography>
              <Slider
                value={formData.targetMaturityLevel}
                onChange={handleMaturityChange}
                min={0}
                max={5}
                step={1}
                marks={maturityLevels.map(level => ({
                  value: level.value,
                  label: `L${level.value}`
                }))}
                valueLabelDisplay="auto"
              />
            </Box>

            <TextField
              fullWidth
              multiline
              rows={4}
              label={t('gap-analysis:assessment.wizard.step2.internalNotes')}
              value={formData.internalNotes}
              onChange={handleChange('internalNotes')}
              placeholder={t('gap-analysis:assessment.wizard.step2.notesPlaceholder')}
            />
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('gap-analysis:assessment.wizard.step3.title')}
            </Typography>
            <Typography variant="body2" color="textSecondary" mb={3}>
              {t('gap-analysis:assessment.wizard.step3.subtitle')}
            </Typography>

            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary">{t('gap-analysis:assessment.wizard.step3.vendorLabel')}</Typography>
                <Typography variant="h6" gutterBottom>{selectedVendor?.name}</Typography>

                <Typography variant="subtitle2" color="textSecondary" mt={2}>{t('gap-analysis:assessment.wizard.step3.frameworkLabel')}</Typography>
                <Typography variant="body1" gutterBottom>{selectedFramework?.name}</Typography>

                <Typography variant="subtitle2" color="textSecondary" mt={2}>{t('gap-analysis:assessment.wizard.step3.targetMaturity')}</Typography>
                <Typography variant="body1">
                  {maturityLevels[formData.targetMaturityLevel].label}
                </Typography>

                {formData.internalNotes && (
                  <>
                    <Typography variant="subtitle2" color="textSecondary" mt={2}>{t('gap-analysis:assessment.wizard.step3.internalNotes')}</Typography>
                    <Typography variant="body2">{formData.internalNotes}</Typography>
                  </>
                )}
              </CardContent>
            </Card>

            {selectedVendor?.tier === 3 ? (
              <Alert severity="info">
                <Typography variant="body2">
                  {t('gap-analysis:assessment.wizard.step3.tier3Info')}
                </Typography>
              </Alert>
            ) : (
              <Alert severity="info">
                <Typography variant="body2">
                  {t('gap-analysis:assessment.wizard.step3.emailInfo', { email: selectedVendor?.contactEmail })}
                </Typography>
              </Alert>
            )}
          </Box>
        );

      default:
        return 'Unknown step';
    }
  };

  return (
    <Box>
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box minHeight={300}>
        {getStepContent(activeStep)}
      </Box>

      <Box display="flex" justifyContent="space-between" mt={4}>
        <Button
          onClick={onCancel}
          disabled={generateMutation.isLoading}
        >
          {t('gap-analysis:assessment.wizard.cancelButton')}
        </Button>
        <Box>
          <Button
            disabled={activeStep === 0 || generateMutation.isLoading}
            onClick={handleBack}
            sx={{ mr: 1 }}
          >
            {t('gap-analysis:assessment.wizard.backButton')}
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={generateMutation.isLoading}
          >
            {generateMutation.isLoading ? (
              <CircularProgress size={24} />
            ) : (
              activeStep === steps.length - 1 ? t('gap-analysis:assessment.wizard.generateButton') : t('gap-analysis:assessment.wizard.nextButton')
            )}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
